/**
 * useAuth — Supabase auth wrapper.
 *
 * Lazy-loads the Supabase client (the chunk only fetches when this hook
 * runs) and defers the initial session check to requestIdleCallback so
 * first paint isn't blocked. Exposes user, session and sign-in/out
 * helpers.
 *
 * Consumers: App.tsx for the profile/avatar UI; community write hooks
 * (useContributions, useDiscussions) read the session for RLS.
 */
import { useState, useEffect, useCallback } from "react";
import type { User, Session } from "@supabase/supabase-js";
import { loadSupabase } from "../lib/supabase.ts";

export interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
}

/** Narrowed because only the expired link has a useful thing to say. */
export type AuthCallbackError = "otp_expired" | "unknown";

/**
 * The error Supabase redirected back with, read from the URL fragment.
 *
 * An expired magic link lands the visitor on
 * `/#error=access_denied&error_code=otp_expired&error_description=…`. The
 * fragment used to be stripped without a word, so they got the plain map,
 * signed out, with nothing to tell them the link was the problem. 17 arrived
 * that way between February and August 2026.
 *
 * Called from a useState initialiser rather than an effect, because init()
 * strips the fragment as soon as the Supabase client has read it.
 */
function readCallbackError(): AuthCallbackError | null {
  const hash = window.location.hash;
  if (!hash.includes("error")) return null;
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  if (!params.get("error") && !params.get("error_code")) return null;
  return params.get("error_code") === "otp_expired" ? "otp_expired" : "unknown";
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({ user: null, session: null, loading: true });
  const [callbackError, setCallbackError] = useState<AuthCallbackError | null>(readCallbackError);
  const clearCallbackError = useCallback(() => setCallbackError(null), []);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: () => void = () => {};

    // If the URL hash includes auth tokens we MUST load Supabase now —
    // the session token has to be persisted before the user navigates
    // away. Otherwise defer to idle so the 46 kB supabase chunk doesn't
    // compete with first paint (Lighthouse flagged 81 % of it as unused
    // on the initial load).
    //
    // IMPORTANT: the hash must stay in the URL until the client exists —
    // supabase-js reads window.location at client creation
    // (detectSessionInUrl) to exchange the tokens. Stripping it up front
    // (as an earlier version did) silently broke email-confirmation and
    // password-recovery links. Clean-up happens after getSession resolves.
    const hash = window.location.hash;
    const isAuthCallback = !!hash && (hash.includes("access_token=") || hash.includes("error=") || hash.includes("error_code="));
    const stripAuthHash = () => {
      const h = window.location.hash;
      if (h && (h.includes("access_token=") || h.includes("error=") || h.includes("error_code="))) {
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      }
    };

    const init = () => {
      if (cancelled) return;
      loadSupabase().then((client) => {
        if (cancelled) return;
        client.auth.getSession().then(({ data: { session } }) => {
          if (isAuthCallback) stripAuthHash();
          if (cancelled) return;
          setState({ user: session?.user ?? null, session, loading: false });
        });
        const { data: { subscription } } = client.auth.onAuthStateChange((_event, session) => {
          setState({ user: session?.user ?? null, session, loading: false });
        });
        unsubscribe = () => subscription.unsubscribe();
      }).catch(() => {
        if (!cancelled) setState(s => ({ ...s, loading: false }));
      });
    };

    if (isAuthCallback) {
      init();
    } else {
      // Mark the unauthenticated state immediately so consumers don't
      // wait — most users aren't signed in, and the session check below
      // will upgrade the state if a session is found.
      setState({ user: null, session: null, loading: false });
      const idle = (window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void }).requestIdleCallback;
      if (idle) idle(init, { timeout: 2500 });
      else setTimeout(init, 1500);
    }

    return () => { cancelled = true; unsubscribe(); };
  }, []);

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    const client = await loadSupabase();
    const { error } = await client.auth.signInWithPassword({ email, password });
    return { error };
  }, []);

  const signUpWithEmail = useCallback(async (email: string, password: string, displayName?: string) => {
    const client = await loadSupabase();
    const { data, error } = await client.auth.signUp({
      email,
      password,
      options: { data: { display_name: displayName } },
    });
    // No session back means Supabase is waiting for the link in the email.
    // The form has to say so: it used to close without a word, and 16 of the
    // first 50 accounts never confirmed (28 Sep 2026).
    return { error, needsConfirm: !error && !data.session };
  }, []);

  /** Sends the sign-up confirmation link again, for an account that never got
   *  or never opened the first one. */
  const resendConfirmation = useCallback(async (email: string) => {
    const client = await loadSupabase();
    const { error } = await client.auth.resend({ type: "signup", email });
    return { error };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const client = await loadSupabase();
    const { error } = await client.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    });
    return { error };
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const client = await loadSupabase();
    const { error } = await client.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });
    return { error };
  }, []);

  const signOut = useCallback(async () => {
    const client = await loadSupabase();
    await client.auth.signOut();
  }, []);

  return { ...state, callbackError, clearCallbackError, signInWithEmail, signUpWithEmail, resendConfirmation, signInWithGoogle, resetPassword, signOut };
}
